import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import { fetchWithAuth } from "../../lib/api";
import { useSearchNeighborhoods } from "../../hooks/usePropertyData";
import { fetchClientProfile } from "../../services/clientProfileService";
import {
  addClientShortlistItem,
  getClientShortlistDetail,
  getOrCreateClientShortlist,
  updateClientShortlistItem,
} from "../../services/clientShortlistService";
import type {
  ClientShortlist,
  ClientShortlistItem,
  ClientShortlistProperty,
} from "../../services/clientShortlistService";
import {
  buildSearchParams,
  filterCardsByName,
  groupUnitsByProperty,
  planPriceLabel,
  prefillFromProfile,
  type CriteriaPrefill,
  type ProfileFieldInput,
  planShortlistWrites,
  type PropertyCard,
  type SearchRow,
} from "./shortlistSearchModel";

/**
 * Search properties and add them to the client's shortlist WITHOUT leaving the
 * thread.
 *
 * 🔴 THIS COMPONENT NEVER SENDS A MESSAGE. On success it hands the caller the
 * shortlist and the caller puts a link in the DRAFT. Automated client messaging
 * is forbidden by standing rule; pre-filling a draft a human then sends is not.
 * There is deliberately no send import in this file, and a test asserts the
 * modal flow cannot reach `sendClientSms`.
 *
 * Deliberately a thin client of the SAME APIs the search page uses rather than
 * an extraction from it: `ApartmentSearch.tsx` is 5,087 lines and is not
 * extractable in one reviewable PR. All logic worth mutating lives in
 * `shortlistSearchModel.ts` and is tested without rendering.
 */

export interface ShortlistSearchModalProps {
  open: boolean;
  clientId: string;
  clientFirstName: string;
  onClose: () => void;
  /** Fired after items are added, so the caller can insert the /s/ link. */
  onSaved: (shortlist: ClientShortlist, addedCount: number) => void;
}

const BEDS = ["", "1", "2", "3", "4"];

const filterKey = (maxPrice: string, minBeds: string, neighborhoods: string[], availableOnly: boolean) =>
  JSON.stringify([maxPrice.trim(), minBeds, [...neighborhoods].sort(), availableOnly]);

function ClientShortlistSearchModal({
  open,
  clientId,
  clientFirstName,
  onClose,
  onSaved,
}: ShortlistSearchModalProps) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const contextEpoch = useRef(0);
  const searchEpoch = useRef(0);
  const contextActive = useRef(open);
  useLayoutEffect(() => {
    contextActive.current = open;
    return () => {
      contextActive.current = false;
      contextEpoch.current += 1;
      searchEpoch.current += 1;
    };
  }, [open]);


  const [maxPrice, setMaxPrice] = useState("");
  const [minBeds, setMinBeds] = useState("");
  const [neighborhoods, setNeighborhoods] = useState<string[]>([]);
  const [availableOnly, setAvailableOnly] = useState(true);
  const [nameQuery, setNameQuery] = useState("");

  // PKT-0841 §4 · prefill from the client's own criteria, once per open, and
  // never over something the agent has already typed.
  const [profileFields, setProfileFields] = useState<ProfileFieldInput[] | null>(null);
  const [prefill, setPrefill] = useState<CriteriaPrefill | null>(null);
  const filtersTouched = useRef(false);
  // PKT-0848 · once the agent has searched, a late prefill must not rewrite
  // the form under results it did not produce; and it auto-runs at most once.
  const searchStarted = useRef(false);
  const touch = () => {
    filtersTouched.current = true;
  };

  // Same cached source as the search page (ApartmentSearch.tsx:1707).
  const { data: availableNeighborhoods = [], isError: neighborhoodsFailed } = useSearchNeighborhoods();

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    fetchClientProfile(clientId)
      .then((profile) => {
        if (!cancelled) setProfileFields(profile.fields || []);
      })
      .catch(() => {
        // No profile, no prefill: the form stays blank exactly as before.
        if (!cancelled) setProfileFields([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, clientId]);

  useEffect(() => {
    if (!open || prefill || !profileFields) return;
    if (!availableNeighborhoods.length && !neighborhoodsFailed) return;
    // The agent got there first: leave their form and results alone.
    if (filtersTouched.current || searchStarted.current) return;
    const next = prefillFromProfile(profileFields, availableNeighborhoods);
    setPrefill(next);
    if (!next.applied) return; // a blank client stays a blank form, no search
    setMaxPrice(next.filters.maxPrice);
    setMinBeds(next.filters.minBeds);
    setNeighborhoods(next.filters.neighborhoods);
    // ONE search, with exactly the values now on screen, so the modal opens
    // on the client's matches (Grant, PKT-0848).
    runSearch({ filters: next.filters });
    // runSearch is intentionally not a dependency: this fires once per open.
  }, [open, prefill, profileFields, availableNeighborhoods, neighborhoodsFailed]);

  const [cards, setCards] = useState<PropertyCard[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [expanded, setExpanded] = useState<string[]>([]);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [existingItems, setExistingItems] = useState<ClientShortlistItem[]>([]);
  const [existingProperties, setExistingProperties] = useState<ClientShortlistProperty[]>([]);
  const [listOpen, setListOpen] = useState(false);
  // PKT-0848 · name mode: every property, filters paused. Fetched once per open
  // (and per Available-only value), then narrowed live as the agent types.
  const [nameCatalog, setNameCatalog] = useState<{ availableOnly: boolean; cards: PropertyCard[] } | null>(null);
  const [nameLoading, setNameLoading] = useState(false);
  // The filters the criteria results were produced with, to flag stale results.
  const [searchedKey, setSearchedKey] = useState<string | null>(null);
  const [existingCount, setExistingCount] = useState(0);
  const [existingReady, setExistingReady] = useState(false);
  const [existingLoadError, setExistingLoadError] = useState<string | null>(null);

  // Load current items before enabling writes. If this fails, POSTing with an
  // empty local view could duplicate an existing property instead of PATCHing
  // its missing floorplan metadata, so save remains fail-closed.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setExistingReady(false);
    setExistingLoadError(null);
    (async () => {
      try {
        const list = await getOrCreateClientShortlist(clientId);
        const detail = await getClientShortlistDetail(list.id);
        if (cancelled) return;
        const activeItems = (detail.items || []).filter((item) => item.status !== "removed");
        setExistingItems(activeItems);
        setExistingProperties(detail.properties || []);
        setExistingCount(activeItems.length);
        setExistingReady(true);
      } catch (loadError: any) {
        if (!cancelled) {
          setExistingItems([]);
          setExistingCount(0);
          setExistingLoadError(
            loadError?.message || "Could not verify the current shortlist. Try again before saving."
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, clientId]);

  // Reset per-open so a previous conversation's selection cannot follow the
  // agent into a different client's thread.
  useEffect(() => {
    if (open) return;
    setCards([]);
    setSaving(false);
    setSearching(false);
    setSelected([]);
    setExpanded([]);
    setSearched(false);
    setError(null);
    setExistingLoadError(null);
    setNameQuery("");
    setNameCatalog(null);
    setSearchedKey(null);
    setListOpen(false);
    setProfileFields(null);
    setPrefill(null);
    filtersTouched.current = false;
    searchStarted.current = false;
  }, [open]);

  const runSearch = useCallback(async (
    override?: { filters?: { maxPrice: string; minBeds: string; neighborhoods: string[] } },
  ) => {
    if (!contextActive.current) return;
    const request = ++searchEpoch.current;
    const isCurrent = () => contextActive.current && request === searchEpoch.current;
    searchStarted.current = true;
    setSearching(true);
    setError(null);
    try {
      // Choosing a neighborhood is what routes the search to the
      // advanced-filters endpoint — see buildSearchParams' fork.
      const f = override?.filters ?? { maxPrice, minBeds, neighborhoods };
      const { endpoint, params } = buildSearchParams({ ...f, availableOnly });
      const rows = await fetchWithAuth<SearchRow[]>(`/${endpoint}?${params}`);
      if (!isCurrent()) return;
      setCards(groupUnitsByProperty(rows || []));
      setSearchedKey(filterKey(f.maxPrice, f.minBeds, f.neighborhoods, availableOnly));
      setSelected([]);
      setExpanded([]);
      setSearched(true);
    } catch (err: any) {
      if (!isCurrent()) return;
      setError(err?.message || "Search failed");
      setCards([]);
      setSearched(true);
    } finally {
      if (isCurrent()) setSearching(false);
    }
  }, [maxPrice, minBeds, neighborhoods, availableOnly]);

  const toggle = (id: string) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const toggleExpanded = (id: string) =>
    setExpanded((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const handleAdd = async () => {
    if (!selected.length || saving || !existingReady) return;
    const context = contextEpoch.current;
    const isCurrentContext = () => contextActive.current && context === contextEpoch.current;
    setSaving(true);
    setError(null);
    try {
      const list = await getOrCreateClientShortlist(clientId);
      const plan = planShortlistWrites(existingItems, allCards, selected, existingCount);

      const failures: string[] = [];
      for (const update of plan.updates) {
        try {
          await updateClientShortlistItem(list.id, update.itemId, {
            metadata: update.metadata,
          });
        } catch {
          failures.push(update.propertyId);
        }
      }
      for (const add of plan.adds) {
        try {
          await addClientShortlistItem(
            list.id,
            add.propertyId,
            add.sortOrder,
            add.metadata
          );
        } catch {
          failures.push(add.propertyId);
        }
      }

      if (!isCurrentContext()) return;
      if (failures.length) {
        // Per-item POSTs, so a partial failure is a real state. Keep the modal
        // open, name what failed, and do NOT hand the caller a link implying a
        // complete list.
        setError(
          `${failures.length} of ${plan.adds.length + plan.updates.length} could not be saved. Nothing was inserted into the message.`
        );
        return;
      }

      onSaved(list, plan.adds.length + plan.updates.length);
      onClose();
    } catch (err: any) {
      if (isCurrentContext()) setError(err?.message || "Could not add to the shortlist");
    } finally {
      if (isCurrentContext()) setSaving(false);
    }
  };

  // Grant: "when im searching for a property I want THAT property." A typed
  // name searches every property and pauses price / beds / areas.
  const nameMode = nameQuery.trim().length > 0;
  useEffect(() => {
    if (!open || !nameMode) return;
    if (nameCatalog?.availableOnly === availableOnly) return;
    let cancelled = false;
    setNameLoading(true);
    const { endpoint, params } = buildSearchParams({
      maxPrice: "",
      minBeds: "",
      neighborhoods: [],
      availableOnly,
    });
    fetchWithAuth<SearchRow[]>(`/${endpoint}?${params}`)
      .then((rows) => {
        if (!cancelled) setNameCatalog({ availableOnly, cards: groupUnitsByProperty(rows || []) });
      })
      .catch((err: any) => {
        if (!cancelled) setError(err?.message || "Search failed");
      })
      .finally(() => {
        if (!cancelled) setNameLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, nameMode, availableOnly, nameCatalog]);

  const nameCards = useMemo(
    () => filterCardsByName(nameCatalog?.cards ?? [], nameQuery),
    [nameCatalog, nameQuery],
  );
  const visibleCards = nameMode ? nameCards : cards;
  // Selections can come from either mode; the save plan sees both.
  const allCards = useMemo(() => {
    const byId = new Map<string, PropertyCard>();
    [...cards, ...(nameCatalog?.cards ?? [])].forEach((c) => {
      if (!byId.has(c.propertyId)) byId.set(c.propertyId, c);
    });
    return Array.from(byId.values());
  }, [cards, nameCatalog]);
  const resultsStale =
    !nameMode && searched && searchedKey !== null &&
    searchedKey !== filterKey(maxPrice, minBeds, neighborhoods, availableOnly);
  // PKT-0943: the budget the SHOWN rows were fetched under (name mode pauses it).
  const searchedMaxPrice: string = (() => {
    if (nameMode || !searchedKey) return "";
    try {
      const parsed = JSON.parse(searchedKey);
      return Array.isArray(parsed) && typeof parsed[0] === "string" ? parsed[0] : "";
    } catch {
      return "";
    }
  })();

  const alreadyOn = (id: string) => existingItems.some((item) => item.property_id === id);
  const selectedPropertyCount = allCards.filter((card) =>
    card.floorPlans.some((floorPlan) => selected.includes(floorPlan.floorPlanId))
  ).length;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" fullScreen={fullScreen}>
      <DialogTitle sx={{ pr: 6, position: "relative" }}>
        <IconButton
          aria-label="Close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
        Add places to {clientFirstName}&apos;s shortlist
        {existingCount > 0 && (
          <Box>
            <Button
              size="small"
              onClick={() => setListOpen((v) => !v)}
              aria-expanded={listOpen}
              data-testid="shortlist-existing-toggle"
              sx={{ px: 0, textTransform: "none", color: "text.secondary", fontWeight: 400 }}
            >
              Shortlist has {existingCount} place{existingCount === 1 ? "" : "s"} · {listOpen ? "hide" : "show"}
            </Button>
            {listOpen && (
              // View only (PKT-0848): no remove or reorder here.
              <Stack spacing={0.5} sx={{ mt: 0.5 }} data-testid="shortlist-existing-list">
                {existingItems.map((item) => {
                  const property = existingProperties.find((p) => p.id === item.property_id);
                  const plans = Array.isArray(item.metadata?.floor_plan_names)
                    ? (item.metadata!.floor_plan_names as unknown[]).map(String)
                    : [];
                  return (
                    <Typography key={item.id} variant="body2">
                      <strong>{property?.name || "A property"}</strong>
                      <Typography component="span" variant="body2" color="text.secondary">
                        {plans.length ? ` · ${plans.join(", ")}` : " · plans not recorded"}
                      </Typography>
                    </Typography>
                  );
                })}
              </Stack>
            )}
          </Box>
        )}
      </DialogTitle>

      <DialogContent dividers>
        <TextField
          label="Property name"
          placeholder="e.g. Nomia"
          value={nameQuery}
          onChange={(e) => setNameQuery(e.target.value)}
          onKeyDown={(e) => {
            // Name mode is live; Enter only runs the criteria search.
            if (e.key === "Enter") {
              e.preventDefault();
              if (!nameQuery.trim()) runSearch();
            }
          }}
          size="small"
          fullWidth
          sx={{ mb: 2 }}
          inputProps={{ "aria-label": "Search by property name" }}
        />
        {nameMode && (
          <Alert severity="info" sx={{ mb: 1 }} data-testid="shortlist-filters-paused">
            Filters paused while searching by name. Clear the name to see {clientFirstName}&apos;s matches.
          </Alert>
        )}
        {prefill?.applied && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }} data-testid="shortlist-prefill-note">
            From {clientFirstName}&apos;s criteria
            {prefill.sources.length ? ` (${prefill.sources.join(", ")})` : ""} — edit to widen.
            {prefill.unmatchedAreas.length > 0 &&
              ` Also mentioned: ${prefill.unmatchedAreas.join(", ")} (not a searchable area).`}
          </Typography>
        )}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{ mb: 2, opacity: nameMode ? 0.5 : 1 }}
          data-testid="shortlist-filters"
        >
          <TextField
            label="Max price"
            disabled={nameMode}
            value={maxPrice}
            onChange={(e) => {
              touch();
              setMaxPrice(e.target.value);
            }}
            size="small"
            inputProps={{ inputMode: "numeric" }}
          />
          <TextField
            select
            label="Min beds"
            disabled={nameMode}
            value={minBeds}
            onChange={(e) => {
              touch();
              setMinBeds(e.target.value);
            }}
            size="small"
            sx={{ minWidth: 120 }}
          >
            {BEDS.map((b) => (
              <MenuItem key={b || "any"} value={b}>
                {b || "Any"}
              </MenuItem>
            ))}
          </TextField>
          <Autocomplete
            multiple
            size="small"
            disabled={nameMode}
            options={availableNeighborhoods}
            value={neighborhoods}
            onChange={(_, newValue) => {
              touch();
              setNeighborhoods(newValue);
            }}
            sx={{ minWidth: 220, flex: 1 }}
            renderInput={(params) => (
              <TextField {...params} label="Neighborhoods" size="small" />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const { key, ...tagProps } = getTagProps({ index });
                return (
                  <Chip
                    key={key}
                    size="small"
                    label={option}
                    {...tagProps}
                    sx={{ borderRadius: 1 }}
                  />
                );
              })
            }
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={availableOnly}
                onChange={(e) => setAvailableOnly(e.target.checked)}
              />
            }
            label="Available only"
            sx={{ whiteSpace: "nowrap", flexShrink: 0, mr: 0 }}
          />
          <Button onClick={() => runSearch()} disabled={searching || nameMode} variant="contained">
            {searching ? "Searching…" : "Search"}
          </Button>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {existingLoadError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {existingLoadError}
          </Alert>
        )}

        {resultsStale && (
          <Typography variant="body2" color="warning.main" sx={{ mb: 1 }} data-testid="shortlist-results-stale">
            Filters changed. Press Search to update these results.
          </Typography>
        )}

        {((searching && !nameMode) || (nameMode && nameLoading)) && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {!nameMode && !searching && !searched && !error && (
          <Typography color="text.secondary">
            {prefill?.applied
              ? "Finding places that fit…"
              : "Type a property name or set filters, then press Search."}
          </Typography>
        )}

        {!nameMode && !searching && searched && cards.length === 0 && !error && (
          <Typography color="text.secondary">
            No properties match — widen the price or drop a filter.
          </Typography>
        )}

        {nameMode && !nameLoading && nameCatalog && nameCards.length === 0 && !error && (
          <Typography color="text.secondary" sx={{ mb: 1 }}>
            No property named &ldquo;{nameQuery.trim()}&rdquo;.
          </Typography>
        )}

        <Stack spacing={1}>
          {visibleCards.map((c) => {
            const on = alreadyOn(c.propertyId);
            const disabled = !c.selectable;
            const isExpanded = expanded.includes(c.propertyId);
            return (
              <Box
                key={c.propertyId}
                sx={{
                  p: 1,
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  opacity: disabled ? 0.6 : 1,
                }}
              >
                {/* Wraps on phones so status chips can never squeeze the
                    property name to nothing (seen at 375px, PKT-0848). */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: { xs: "wrap", sm: "nowrap" } }}>
                  <Button
                    size="small"
                    onClick={() => toggleExpanded(c.propertyId)}
                    disabled={disabled}
                    aria-label={`${isExpanded ? "Hide" : "Show"} floorplans for ${c.propertyName}`}
                  >
                    {isExpanded ? "Hide plans" : "Choose plans"}
                  </Button>
                  <Box sx={{ flex: "1 1 140px", minWidth: 0 }}>
                    <Typography noWrap>{c.propertyName}</Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {c.neighborhood || "—"}
                      {c.rentLow !== null &&
                        ` · $${c.rentLow.toLocaleString()}${
                          c.rentHigh !== null && c.rentHigh !== c.rentLow
                            ? `–$${c.rentHigh.toLocaleString()}`
                            : ""
                        }`}
                    </Typography>
                  </Box>
                  {on && <Chip size="small" label="On list — update plans" />}
                  {!c.selectable && (
                    <Stack direction="row" spacing={0.5}>
                      {!c.pricingCurrent && (
                        <Chip size="small" color="warning" label="Pricing not current" />
                      )}
                      {!c.floorplanIdsComplete && (
                        <Chip size="small" label="No current floorplans" />
                      )}
                    </Stack>
                  )}
                </Box>
                {isExpanded && c.selectable && (
                  <Stack spacing={0.5} sx={{ mt: 1, pl: 1 }}>
                    {c.floorPlans.map((floorPlan) => (
                      <Box
                        key={floorPlan.floorPlanId}
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Checkbox
                          checked={selected.includes(floorPlan.floorPlanId)}
                          onChange={() => toggle(floorPlan.floorPlanId)}
                          inputProps={{
                            "aria-label": `Select ${floorPlan.modelName} at ${c.propertyName}`,
                          }}
                        />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={700}>
                            {floorPlan.modelName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {floorPlan.bedrooms ?? "—"} bd · {floorPlan.bathrooms ?? "—"} ba
                            {floorPlan.squareFeet ? ` · ${floorPlan.squareFeet.toLocaleString()} sq ft` : ""}
                            {` · ${planPriceLabel(floorPlan, searchedMaxPrice)}`}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                )}
              </Box>
            );
          })}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleAdd}
          variant="contained"
          disabled={!selected.length || saving || !existingReady}
        >
          {saving
            ? "Saving…"
            : !selected.length
              ? "Choose plans to add"
              : `Add ${selected.length} ${selected.length === 1 ? "floorplan" : "floorplans"} from ${selectedPropertyCount} ${selectedPropertyCount === 1 ? "property" : "properties"}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}


// Keep results, criteria and selections owned by the visible client.
export default function ShortlistSearchModal(props: ShortlistSearchModalProps) {
  return <ClientShortlistSearchModal key={props.clientId} {...props} />;
}
